import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  Select,
  InputLabel,
  FormControl,
  Snackbar,

  Alert,
} from "@mui/material";
import { DataGridPro, GridActionsCellItem } from "@mui/x-data-grid-pro";
import { Visibility, Delete, Add, Subject } from "@mui/icons-material";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import useSharedLink from "../../SharedLink";
import axiosInstance from "../../api/axiosInstance";

// Define the RespondDialog component
const RespondDialog = ({ open, onClose, email, subject, sharedLink , showSnackbar}) => {
  const [message, setMessage] = useState("");
  

  const handleSend = () => {
    // JSON payload for the email
    const emailPayload = {
      to: [email],
      subject: subject,
      body: message,
    };

    // API call to send the email
    axiosInstance.post(`${sharedLink}/api/send-email`, emailPayload, { withCredentials: true })
      .then((response) => {
        //console.log('Email sent successfully:', response.data); // Log the success response
        showSnackbar("Email sent successfully!", "success"); // Trigger success Snackbar
        onClose(); // Close the dialog
      })
      .catch((error) => {
        console.error('Error sending email:', error); // Log any error
        showSnackbar("Failed to send email. Please try again.", "error");
      });
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Respond</DialogTitle>
      <DialogContent>
        <TextField
          label="Email"
          value={email}
          fullWidth
          margin="dense"
          variant="outlined"
          disabled // Disable the email field
        />

        <TextField
          label="Subject"
          value={subject} // Bind subject prop
          fullWidth
          margin="dense"
          variant="outlined"
          disabled // Disable the subject field
        />

        <TextField
          label="Message"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          fullWidth
          margin="dense"
          variant="outlined"
          multiline
          rows={4}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleSend} color="primary">
          Send
        </Button>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

const AllEmail = () => {
  const sharedLink = useSharedLink();
  const [rows, setRows] = useState([]);
  const [allRows, setAllRows] = useState([]); // State to keep all rows
  const [open, setOpen] = useState(false); // State to control the dialog
  const [contactDetails, setContactDetails] = useState({}); // State for contact details
  const [showInactive, setShowInactive] = useState(false); // State to control the checkbox
  const [respondOpen, setRespondOpen] = useState(false); // State for Respond dialog
  const [snackbarOpen, setSnackbarOpen] = useState(false); // State for Snackbar
  const [snackbarMessage, setSnackbarMessage] = useState(""); // State for Snackbar message
  const [snackbarSeverity, setSnackbarSeverity] = useState("success"); // State for Snackbar severity

  const navigate = useNavigate();

  useEffect(() => {
    //console.log(contactDetails);
    fetchAllContacts();
  }, [contactDetails]);

  useEffect(() => {
    fetchAllContacts();
  }, [showInactive]); // Re-fetch contacts when checkbox state changes

  const fetchAllContacts = () => {
    axiosInstance
      .post(
        `${sharedLink}/api/abanotes/getallcontactus`,
        {},
        { withCredentials: true }
      )
      .then((response) => {
        let filteredContacts;
        if (showInactive) {
          // Show only inactive contacts when checkbox is checked
          filteredContacts = response.data.filter(
            (contact) => contact.active === false
          );
        } else {
          // Filter the response data to include only active contacts
          filteredContacts = response.data.filter(
            (contact) => contact.active === true
          );
        }
        // Log the filtered data for debugging
        //console.log("Filtered Contacts:", filteredContacts);
        // Set the state with the filtered data
        setRows(filteredContacts);
        setAllRows(filteredContacts);
      })
      .catch((error) => {
        console.error("Error fetching contacts:", error);
      });
  };

  const handleView = (id) => {
    //console.log(`Fetching details for ID: ${id}`);
    axiosInstance
      .post(
        `${sharedLink}/api/abanotes/getcontactus`,
        { id: id }, // Correctly format the body as a JSON string
        {
          headers: {
            "Content-Type": "application/json", // Set the content type header for JSON
          },
          withCredentials: true,
        }
      )
      .then((response) => {
        // Set the state variables with the fetched data
        //console.log("API Response:", response.data); // Log the entire response to inspect its structure

        const contact = response.data[0];
        const { date, email, id, message, name, phone, subject, active } =
          contact;

        const formattedDate = new Date(date).toISOString().split("T")[0];

        setContactDetails({
          date: formattedDate, // Use the formatted date
          email,
          id,
          message,
          name,
          phone,
          subject,
          active, // Add the active field
        });
        // //console.log(contactDetails);

        setOpen(true); // Open the dialog after setting the state
      })
      .catch((error) => {
        console.error("Error fetching contact details:", error);
      });
  };

  const handleDelete = (id) => {
    // Show confirmation alert for delete action
    if (window.confirm("Are you sure you want to delete this contact?")) {
      //console.log(`Deactivating contact with ID: ${id}`);
      axiosInstance
        .post(
          `${sharedLink}/api/abanotes/deactivatecontactus`,
          { id: id }, // Pass the id to the API
          {
            headers: {
              "Content-Type": "application/json", // Set the content type header for JSON
            },
            withCredentials: true,
          }
        )
        .then((response) => {
          //console.log(`Contact with ID: ${id} deactivated successfully`);
          // Update the state to remove the deactivated contact from the list
          setRows((prevRows) => prevRows.filter((row) => row.id !== id));
          setAllRows((prevAllRows) => prevAllRows.filter((row) => row.id !== id));
          // Show success Snackbar
          setSnackbarMessage("Contact deactivated successfully");
          setSnackbarSeverity("success");
          setSnackbarOpen(true);
        })
        .catch((error) => {
          console.error("Error deactivating contact:", error);
          // Show error Snackbar
          setSnackbarMessage("Failed to deactivate contact");
          setSnackbarSeverity("error");
          setSnackbarOpen(true);
        });
    }
  };

  // Function to handle activating the contact
  const handleActivate = (id) => {
    // Show confirmation alert for add action
    if (window.confirm("Are you sure you want to add this contact?")) {
      //console.log(`Activating contact with ID: ${id}`);
      axiosInstance
        .post(
          `${sharedLink}/api/abanotes/activatecontactus`,
          { id: id }, // Pass the id to the API
          {
            headers: {
              "Content-Type": "application/json", // Set the content type header for JSON
            },
            withCredentials: true,
          }
        )
        .then((response) => {
          //console.log(`Contact with ID: ${id} activated successfully`);
          // Update the state to add the activated contact to the list
          setRows((prevRows) =>
            prevRows.map((row) =>
              row.id === id ? { ...row, active: true } : row
            )
          );
          setAllRows((prevAllRows) =>
            prevAllRows.map((row) =>
              row.id === id ? { ...row, active: true } : row
            )
          );
          // Show success Snackbar
          setSnackbarMessage("Contact activated successfully");
          setSnackbarSeverity("success");
          setSnackbarOpen(true);
        })
        .catch((error) => {
          console.error("Error activating contact:", error);
          // Show error Snackbar
          setSnackbarMessage("Failed to activate contact");
          setSnackbarSeverity("error");
          setSnackbarOpen(true);
        });
    }
  };

  // Close the dialog and reset the state variables
  const handleClose = () => {
    setOpen(false);
    setContactDetails({}); // Clear contact details when closing the dialog
  };

  // Open the Respond dialog
  const handleRespond = () => {
    setRespondOpen(true); // Open Respond dialog
  };

  // Close the Respond dialog
  const handleRespondClose = () => {
    setRespondOpen(false); // Close Respond dialog
  };

  const handleActiveChange = (event) => {
    const newActive = event.target.value === "true";
    setContactDetails((prevDetails) => ({
      ...prevDetails,
      active: newActive,
    }));
  };

  // Handle Snackbar close event
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // Columns for the DataGridPro
  const columns = [
    { field: "name", headerName: "Name", width: 200 },
    { field: "email", headerName: "Email", width: 200 },
    {
      field: "date",
      headerName: "Date",
      width: 300,
      valueGetter: (params) =>
        new Date(params.value).toISOString().split("T")[0], // Format the date to remove time portion
    },
    { field: "active", headerName: "Active", width: 200 },
    { field: "phone", headerName: "Phone Number", width: 200 },
    {
      field: "actions",
      headerName: "Actions",
      type: "actions",
      width: 100,
      getActions: (params) => {
        const actions = [
          <GridActionsCellItem
            key={`view-${params.id}`} // Adding key prop
            icon={<Visibility />}
            label="View"
            onClick={() => handleView(params.id)}
          />
        ];
  
        // Add the Delete icon only if the email is active
        if (params.row.active) {
          actions.push(
            <GridActionsCellItem
              key={`delete-${params.id}`} // Adding key prop
              icon={<Delete />}
              label="Delete"
              onClick={() => handleDelete(params.id)} // Add delete handler
            />
          );
        }
  
        // Add the Add icon only if the email is not active
        if (!params.row.active) {
          actions.push(
            <GridActionsCellItem
              key={`add-${params.id}`} // Adding key prop
              icon={<Add />}
              label="Add"
              onClick={() => handleActivate(params.id)} // Add activate handler
            />
          );
        }
  
        return actions;
      }
    }
  ];
  

  return (
    <Box sx={{ width: "80%", mt: 20, ml: "auto", mr: "auto",backdropFilter: "blur(10px)"  }}>
      <Typography
        variant="h4"
        sx={{ mb: 10, textAlign: "center", fontWeight: "bold", fontSize: 35 }}
      >
        All Contact Us
      </Typography>
      <Box sx={{ height: "calc(90vh - 260px)", width: "100%" }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              name="showInactive"
              color="primary"
            />
          }
          label="Show Inactive Emails"
        />

        <DataGridPro
          rows={rows}
          columns={columns}
          pageSize={5}
          // checkboxSelection
          getRowId={(row) => row.id}
          className="animated-input"
          sx={{
            boxShadow: 5,
            border: "none",
            "& .MuiDataGrid-cell:hover": {
              color: "primary.main",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              color: "white !important",
              fontWeight: "bold !important",
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#e9a132 !important",
              borderBottom: "white !important",
              alignContent: "center",
              alignItems: "center",
              display: "flex",
              height: "100%",
            },
            "& .MuiDataGrid-columnSeparator": {
              visibility: "hidden",
            },
            "& .MuiDataGrid-toolbarContainer": {
              color: "white !important",
              justifyContent: "space-between",
            },
            "& .MuiDataGrid-footerContainer": {
              borderTop: "none",
              color: "white !important",
              fontWeight: "bold !important",
              backgroundColor: "#e9a132 !important",
            },
            "& .MuiTablePagination-root": {
              color: "white !important",
            },
            "& .MuiIconButton-root": {
              color: "black !important",
            },
            "& .MuiDataGrid-selectedRowCount": {
              color: "white !important",
            },
          }}
        />
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "10px 20px",
          backgroundColor: "white",
          color: "black",
          borderTop: "1px solid #fff",
          borderColor: "yellow",
        }}
      >
        {/* <Typography variant="subtitle1">Total Rows: {rows.length}</Typography> */}
      </Box>
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Contact Details</DialogTitle>
        <DialogContent>
          <TextField
            label="Name"
            value={contactDetails.name || ""} // Bind state variable
            fullWidth
            margin="dense"
            variant="outlined"
            disabled={!contactDetails.active} // Disable when active is false
          />
          <TextField
            label="Email"
            value={contactDetails.email || ""} // Bind state variable
            fullWidth
            margin="dense"
            variant="outlined"
            disabled={!contactDetails.active} // Disable when active is false
          />
          <TextField
            label="Phone"
            value={contactDetails.phone || ""} // Bind state variable
            fullWidth
            margin="dense"
            variant="outlined"
            disabled={!contactDetails.active} // Disable when active is false
          />
          <TextField
            label="Subject"
            value={contactDetails.subject || ""} // Bind state variable
            fullWidth
            margin="dense"
            variant="outlined"
            disabled={!contactDetails.active} // Disable when active is false
          />
          <TextField
            label="Message"
            value={contactDetails.message || ""} // Bind state variable
            fullWidth
            margin="dense"
            variant="outlined"
            multiline
            rows={4}
            disabled={!contactDetails.active} // Disable when active is false
          />
          <TextField
            label="Date"
            value={contactDetails.date || ""} // Bind state variable
            fullWidth
            margin="dense"
            variant="outlined"
            disabled={!contactDetails.active} // Disable when active is false
          />

          {/* <FormControl fullWidth margin="dense">
            <InputLabel htmlFor="active" shrink>
              Active
            </InputLabel>
            <Select
              native
              value={contactDetails.active || ""}
              inputProps={{
                name: "active",
                id: "active",
              }}
              onChange={handleActiveChange} // Handle change event
            >
              <option value={true}>True</option>
              <option value={false}>False</option>
            </Select>
          </FormControl> */}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleRespond} color="primary">
            Respond
          </Button>{" "}
          {/* Respond Button */}
          <Button onClick={handleClose}>Close</Button>
        </DialogActions>
      </Dialog>
      <RespondDialog
        open={respondOpen}
        onClose={handleRespondClose}
        email={contactDetails.email}
        subject={contactDetails.subject} // Pass the subject to the RespondDialog
        sharedLink={sharedLink} // Pass the sharedLink
        showSnackbar={(message, severity) => {
          setSnackbarMessage(message);
          setSnackbarSeverity(severity);
          setSnackbarOpen(true);
        }} // Pass the showSnackbar function
      />
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }} // Set position to top center
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AllEmail;
